// Extracted verbatim from the v3 monolith (see BIRDSEYE.md module split plan).

import { isReducedMotion } from './utils.js';

export var _isVisibleInDom = function (element) {
  return (
    !!element &&
    this.list &&
    this.list.contains(element) &&
    element.style.display !== 'none' &&
    !element.hidden
  );
};

export var _captureVisibleRects = function () {
  var rects = new Map();

  this.items.forEach(
    function (record) {
      if (!this._isVisibleInDom(record.el)) {
        return;
      }

      rects.set(record.id, record.el.getBoundingClientRect());
    }.bind(this)
  );

  return rects;
};

export var _isGsapAnimationEnabled = function () {
  return (
    this.animationMode === 'gsap' &&
    window.gsap &&
    typeof window.gsap.fromTo === 'function'
  );
};

export var _shouldAnimate = function () {
  return this.animationMode !== 'none' && !isReducedMotion();
};

export var _stopElementAnimation = function (element) {
  var active = this._animationMap.get(element);
  if (!active) {
    return;
  }

  if (typeof active.kill === 'function') {
    active.kill();
  }
  if (typeof active.cancel === 'function') {
    active.cancel();
  }
  this._animationMap.delete(element);
};

export var _animateElement = function (element, keyframes, timing) {
  this._stopElementAnimation(element);
  var self = this;

  if (this._isGsapAnimationEnabled()) {
    var fromFrame = keyframes[0] || {};
    var toFrame = keyframes[keyframes.length - 1] || {};
    var durationMs = timing.duration || this.animationDuration;
    var delayMs = timing.delay || 0;
    var ease = timing.easing || this.animationEasing;
    var fromFilter =
      fromFrame.blur !== undefined ? 'blur(' + fromFrame.blur + 'px)' : undefined;
    var toFilter =
      toFrame.blur !== undefined ? 'blur(' + toFrame.blur + 'px)' : undefined;

    var fromVars = {
      x: fromFrame.translateX || 0,
      y: fromFrame.translateY || 0,
      scale: fromFrame.scale !== undefined ? fromFrame.scale : 1,
      opacity: fromFrame.opacity !== undefined ? fromFrame.opacity : 1,
    };
    var toVars = {
      x: toFrame.translateX || 0,
      y: toFrame.translateY || 0,
      scale: toFrame.scale !== undefined ? toFrame.scale : 1,
      opacity: toFrame.opacity !== undefined ? toFrame.opacity : 1,
      duration: durationMs / 1000,
      delay: delayMs / 1000,
      ease: ease,
      overwrite: 'auto',
      onComplete: function () {
        element.style.transform = '';
        element.style.opacity = '';
        element.style.filter = '';
        self._animationMap.delete(element);
      },
    };
    if (fromFilter) {
      fromVars.filter = fromFilter;
    }
    if (toFilter) {
      toVars.filter = toFilter;
    }

    var tween = window.gsap.fromTo(element, fromVars, toVars);

    this._animationMap.set(element, tween);
    return;
  }

  var normalizedFrames = keyframes.map(function (frame) {
    var transformParts = [];

    if (frame.translateX || frame.translateY) {
      transformParts.push(
        'translate(' + (frame.translateX || 0) + 'px, ' + (frame.translateY || 0) + 'px)'
      );
    }
    if (frame.scale !== undefined) {
      transformParts.push('scale(' + frame.scale + ')');
    }

    var filterValue = frame.blur !== undefined ? 'blur(' + frame.blur + 'px)' : 'none';

    return {
      transform: transformParts.length ? transformParts.join(' ') : 'none',
      opacity: frame.opacity !== undefined ? String(frame.opacity) : '1',
      filter: filterValue,
    };
  });

  if (typeof element.animate !== 'function') {
    element.style.transform = '';
    element.style.opacity = '';
    element.style.filter = '';
    return;
  }

  var animation = element.animate(normalizedFrames, {
    duration: timing.duration || this.animationDuration,
    easing: timing.easing || this.animationEasing,
    delay: timing.delay || 0,
    // Backwards only: holds the first frame through the stagger delay. A forwards fill
    // would keep the finished effect alive forever, overriding the card's own transform /
    // opacity — Webflow IX2 hover moves and CSS :hover transforms stop working. The last
    // frame is the element's natural state, so ending the effect causes no jump.
    fill: 'backwards',
  });

  this._animationMap.set(element, animation);
  animation.onfinish = function () {
    element.style.transform = '';
    element.style.opacity = '';
    element.style.filter = '';
    self._animationMap.delete(element);
  };
  animation.oncancel = function () {
    self._animationMap.delete(element);
  };
};

export var _animateItems = function (beforeRects, stableItems, enteringItems) {
  var self = this;
  var visibleRecords = stableItems.concat(enteringItems);

  stableItems.forEach(function (record, index) {
    var first = beforeRects.get(record.id);
    if (!first) {
      return;
    }

    var last = record.el.getBoundingClientRect();
    var dx = first.left - last.left;
    var dy = first.top - last.top;

    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
      return;
    }

    self._animateElement(
      record.el,
      [
        {
          translateX: dx,
          translateY: dy,
          scale: self.animationMoveScaleFrom,
          opacity: 1,
        },
        {
          translateX: 0,
          translateY: 0,
          scale: self.animationMoveScaleTo,
          opacity: 1,
        },
      ],
      {
        duration: self.animationDuration,
        easing: self.animationEasing,
        delay: index * self.animationStagger,
      }
    );
  });

  if (!this.animationFadeIn) {
    return;
  }

  enteringItems.forEach(function (record, index) {
    var delay = (visibleRecords.length + index) * self.animationStagger;

    self._animateElement(
      record.el,
      [
        {
          translateX: 0,
          translateY: self.animationYOffset,
          scale: self.animationEnterScaleFrom,
          blur: self.animationEnterBlurFrom,
          opacity: 0,
        },
        {
          translateX: 0,
          translateY: 0,
          scale: self.animationEnterScaleTo,
          blur: self.animationEnterBlurTo,
          opacity: 1,
        },
      ],
      {
        duration: Math.round(self.animationDuration * 0.9),
        easing: self.animationEasing,
        delay: delay,
      }
    );
  });
};

